import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../services/API";

export const fetchCategories = createAsyncThunk("categories/fetchAll", async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get("/categories/");
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: "Failed to fetch categories" });
    }
});

export const fetchUserCategories = createAsyncThunk("categories/fetchUser", async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get("/profile/");
        return response.data.selected_categories ?? [];
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: "Failed to fetch profile" });
    }
});

export const selectCategories = createAsyncThunk("categories/select", async (slugs, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post("/select_categories/", { categories: slugs });
        return { slugs, message: response.data.message };
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: "Failed to save categories" });
    }
});

export const skipCategories = createAsyncThunk("categories/skip", async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post("/skip_categories/");
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: "Failed to skip categories" });
    }
});

export const removeCategory = createAsyncThunk("categories/remove", async (slug, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post("/remove_category/", { category: slug });
        return { slug, message: response.data.message };
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: "Failed to remove category" });
    }
});

const CategorySlice = createSlice({
    name: "Category",
    initialState: {
        allCategories: [],
        selectedCategories: [],
        loading: false,
        userLoading: false,
        submitting: false,
        error: null,
    },
    reducers: {
        clearCategoryError(state) {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCategories.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(fetchCategories.fulfilled, (state, action) => { state.loading = false; state.allCategories = action.payload; })
            .addCase(fetchCategories.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

            .addCase(fetchUserCategories.pending, (state) => { state.userLoading = true; })
            .addCase(fetchUserCategories.fulfilled, (state, action) => { state.userLoading = false; state.selectedCategories = action.payload; })
            .addCase(fetchUserCategories.rejected, (state) => { state.userLoading = false; })

            .addCase(selectCategories.pending, (state) => { state.submitting = true; state.error = null; })
            .addCase(selectCategories.fulfilled, (state, action) => {
                state.submitting = false;
                const slugSet = new Set(action.payload.slugs);
                state.selectedCategories = state.allCategories
                    .flatMap((g) => g.items)
                    .filter((item) => slugSet.has(item.slug));
            })
            .addCase(selectCategories.rejected, (state, action) => { state.submitting = false; state.error = action.payload; })

            .addCase(skipCategories.pending, (state) => { state.submitting = true; })
            .addCase(skipCategories.fulfilled, (state) => { state.submitting = false; })
            .addCase(skipCategories.rejected, (state) => { state.submitting = false; })

            .addCase(removeCategory.pending, (state) => { state.submitting = true; state.error = null; })
            .addCase(removeCategory.fulfilled, (state, action) => {
                state.submitting = false;
                state.selectedCategories = state.selectedCategories.filter((c) => c.slug !== action.payload.slug);
            })
            .addCase(removeCategory.rejected, (state, action) => { state.submitting = false; state.error = action.payload; });
    },
});

export const { clearCategoryError } = CategorySlice.actions;
export default CategorySlice.reducer;
